import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import { flexCenter } from '../../../commonUI/cssClasses';
import {
  UploadedBlockTypes,
  selectUploadedBlockGeoJSON,
  setUploadedBlockGeoJSON,
} from '../../onboardingSlice';
import {
  cleanAndFormatGeoJSON,
  isValidFeatureCollection,
} from '../../onboardingUtils';

interface Status {
  message: string;
  type: 'success' | 'error' | 'idle';
}

const defaultStatus: Status = {
  message: 'No file uploaded yet.',
  type: 'idle',
};

// Define acceptable file types
const fileTypes: string[] = ['GEOJSON'];

const BlockUploader: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const uploadedBlockGeoJSON = useAppSelector(selectUploadedBlockGeoJSON);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>(defaultStatus);
  const [geojsonData, setGeojsonData] = useState<UploadedBlockTypes>(null);

  // Reset the status message if the file in redux changes to null which indicates a successful upload
  useEffect(() => {
    if (uploadedBlockGeoJSON === null) {
      resetStatus();
    }
  }, [uploadedBlockGeoJSON]);

  const resetStatus = () => {
    setStatus(defaultStatus);
  };

  const handleChange = (newFile: File) => {
    setFile(newFile);

    // Initialize FileReader to read the uploaded file
    const reader = new FileReader();

    // Add an event listener for when the file read is complete
    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target && event.target.result) {
        try {
          // Parse the file content to JSON
          const content = JSON.parse(event.target.result as string);
          // Validate the GeoJSON data
          if (isValidFeatureCollection(content)) {
            // Clean the data and strip out unnecessary customer data
            const cleanData = cleanAndFormatGeoJSON(content);
            setGeojsonData(cleanData);
            setStatus({
              message: `${t('Map preview in red.', {
                ns: 'common',
              })} ${t('File', {
                ns: 'common',
              })}: ${newFile?.name} `,
              type: 'success',
            });
          } else {
            setFile(null);
            setGeojsonData(null);
            setStatus({
              message: `${t('Invalid GeoJSON file.', { ns: 'common' })}!`,
              type: 'error',
            });
          }
        } catch (error) {
          setFile(null);
          setGeojsonData(null);
          console.error('Invalid JSON content:', error);
          console.log('status', status);
          console.log('file', file);
          console.log('geojsonData', geojsonData); // Log the GeoJSON data for debugging
          setStatus({
            message: `${t('Error parsing file: ', { ns: 'common' })} ${
              newFile.name
            }.`,
            type: 'error',
          });
        }
      }
    };

    // Start reading the file
    reader.readAsText(newFile);
  };

  const onTypeError = () => {
    setStatus({
      message: `${t('Invalid file type.', { ns: 'common' })}`,
      type: 'error',
    });
  };

  const onSizeError = () => {
    setStatus({
      message: `${t('File size error, must be between 0 - 10 MB.', {
        ns: 'common',
      })}`,
      type: 'error',
    });
  };

  // Update the Redux store with the GeoJSON data if valid
  useEffect(() => {
    if (status.type === 'success' && geojsonData) {
      console.log('Setting uploaded block geoJSON', geojsonData);
      dispatch(setUploadedBlockGeoJSON(geojsonData));
    } else {
      dispatch(setUploadedBlockGeoJSON(null));
    }
  }, [dispatch, geojsonData, status]);

  return (
    <Box sx={{ width: '100%', ...flexCenter, flexDirection: 'column' }}>
      <FileUploader
        handleChange={handleChange}
        name='file'
        types={fileTypes}
        minSize={0}
        maxSize={10}
        ondrop={resetStatus}
        onTypeError={onTypeError}
        onSizeError={onSizeError}
      />
      <Typography variant='body1' sx={{ mt: '1rem' }}>
        {status.message}
      </Typography>
    </Box>
  );
};

export default BlockUploader;
